import React, { createContext, useContext, useMemo, useState } from 'react';

import en from './en.json';
import es from './es.json';

type Locale = 'en' | 'es';
type Bundle = Record<string, string>;

const BUNDLES: Record<Locale, Bundle> = {
  en: en as Bundle,
  es: es as Bundle,
};

interface I18nContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

interface I18nProviderProps {
  initialLocale?: Locale;
  children: React.ReactNode;
}

/**
 * Lightweight i18n. Backend-rendered strings (notification titles/bodies) come
 * through pre-localized — this hook only translates UI chrome.
 */
export const I18nProvider: React.FC<I18nProviderProps> = ({ initialLocale, children }) => {
  const fromStorage = (typeof window !== 'undefined' ? window.localStorage.getItem('preferred_locale') : null) as Locale | null;
  const [locale, _setLocale] = useState<Locale>(initialLocale ?? fromStorage ?? 'en');

  const value = useMemo<I18nContextValue>(() => {
    const setLocale = (l: Locale) => {
      _setLocale(l);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('preferred_locale', l);
      }
    };
    const t = (key: string, params?: Record<string, string | number>): string => {
      const bundle = BUNDLES[locale] ?? BUNDLES.en;
      const raw = bundle[key] ?? BUNDLES.en[key] ?? key;
      if (!params) return raw;
      // Simple {name} and {count, plural, ...} substitution.
      return interpolate(raw, params);
    };
    return { locale, setLocale, t };
  }, [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}

// ---- helpers ----

function interpolate(template: string, params: Record<string, string | number>): string {
  // Handle {name} and basic ICU-like {count, plural, one {x} other {y}}.
  return template.replace(/\{([^}]+)\}/g, (_match, group) => {
    const trimmed = group.trim();
    // plural form: "count, plural, one {x} other {y}"
    if (trimmed.includes(', plural,')) {
      const [name, , ...rest] = trimmed.split(/\s*,\s*/);
      const value = params[name];
      const n = typeof value === 'number' ? value : Number(value);
      const text = rest.join(', ');
      const oneMatch = /one\s*\{([^}]*)\}/.exec(text);
      const otherMatch = /other\s*\{([^}]*)\}/.exec(text);
      const oneText = oneMatch?.[1] ?? '';
      const otherText = otherMatch?.[1] ?? '';
      return (n === 1 ? oneText : otherText).replace(/#/g, String(n));
    }
    const v = params[trimmed];
    return v === undefined ? `{${trimmed}}` : String(v);
  });
}
